import { ChangeEvent, FormEvent, useState, forwardRef } from 'react';
import styled from 'styled-components';
import Constants from '../../../constants/constants';
import Input from '../Input';
import Button from '../Button';

const Card = styled.div(({ theme }) => {
  const { color, shadow } = theme;

  return `
    background-color: ${color.grayscale[1000]};
    padding: 38px 32px 26px;
    min-width: 300px;
    max-width: 500px;
    margin: 0 auto;

    @media screen and (min-width: 500px) {
      border-radius: 16px;
      box-shadow: ${shadow.dialog};
    }
  `;
});

const Heading = styled.h1(({ theme }) => {
  const { type, color } = theme;

  return `
    font-family: ${type.face};
    font-size: ${type.size.larger};
    color: ${color.grayscale[0]};
    margin-bottom: 0.5rem;
  `;
});

const Instructions = styled.p(({ theme }) => {
  const { type } = theme;

  return `
    font-size: ${type.size.small};
    margin: 1rem 0;
  `;
});

const ResendText = styled.p(({ theme }) => {
  const { type } = theme;

  return `
    font-size: ${type.size.tiny};
    text-align: center;
    padding: 1rem 0 0;
  `;
});

const ResendButton = styled.button(({ theme }) => {
  const { color, type } = theme;

  return `
    display: inline-block;
    font-family: ${type.face};
    color: ${color.primary.regular};
    text-decoration: none;
    background: transparent;
    border: 0;

    &:hover {
      text-decoration: underline;
    }
  `;
});

const ButtonWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: center;
  gap: 1rem;
  margin: 1rem 0;
`;

export interface IOTPPrompt {
  submitCallback: (otpCode?: string) => void;
  cancelCallback: () => void;
  resendCallback: () => void;
  error: string | false;
  lastPhoneDigits: string;
}

const OTPPrompt = forwardRef<HTMLInputElement, IOTPPrompt>(
  ({ submitCallback, cancelCallback, resendCallback, error, lastPhoneDigits }, ref) => {
    const [otpInput, setOtpInput] = useState<string>('');

    const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
      if (e.currentTarget.value.length > 6) {
        return false;
      }

      setOtpInput(e.currentTarget.value.replace(/[^0-9]+/g, ''));
    };

    const handleSubmit = (e: FormEvent) => {
      e.preventDefault();
      submitCallback(otpInput);
    };

    const handleResend = () => {
      resendCallback();
    };

    const handleCancel = () => {
      cancelCallback();
    };

    return (
      <Card>
        <Heading>User Verification</Heading>

        <Instructions aria-live="polite">
          A message with an SMS message to (XXX) XXX-XX{lastPhoneDigits} with a {Constants.OTP_CODE_LENGTH}-digit
          verification code.
        </Instructions>

        <form onSubmit={handleSubmit}>
          <Input
            type="text"
            name="otp"
            id="otp_input"
            label="Verification Code"
            value={otpInput}
            inputMode="numeric"
            onChange={handleChange}
            required
            maxLength={Constants.OTP_CODE_LENGTH}
            minLength={Constants.OTP_CODE_LENGTH}
            pattern="[0-9]*"
            errors={error ? error : ''}
            invalid={error ? true : false}
            ref={ref}
          />

          <ButtonWrapper>
            <Button type="button" onClick={handleCancel} hollow secondary>
              Cancel
            </Button>

            <Button type="submit" disabled={otpInput?.length !== Constants.OTP_CODE_LENGTH}>
              Verify
            </Button>
          </ButtonWrapper>

          <ResendText>
            Didn't receive the message?
            <br />
            <ResendButton type="button" onClick={handleResend}>
              Request a new verification code
            </ResendButton>
          </ResendText>
        </form>
      </Card>
    );
  }
);

export default OTPPrompt;
